import { Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Subscription, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { IPartnerConfig } from 'src/app/models/partner-config.model';
import { LocationStrategy } from '@angular/common';
import { ProductModel } from 'src/app/models/product.model';
import { LanguageModel } from 'src/app/models/language.model';
import { ProductOptionModel } from 'src/app/models/product-option.model';
import { ProductTypeId } from 'src/app/models/enums/product-type.enum';
import { of } from 'rxjs/internal/observable/of';
import { CartProductInfo, CartProductInfoProvider } from 'src/app/providers/cart-product-info.provider';
import { BookingService } from 'src/app/services/booking.service';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent implements OnInit, OnDestroy {
  public products: ProductModel[];
  public languages: LanguageModel[];
  public options: ProductOptionModel[];
  public interval: any;
  public loaded: boolean;
  public pageConfig: IPartnerConfig;
  public baseUrl: string = '/';
  public currentDate = new Date();

  private subscribtions: Subscription;

  constructor(
    private booking: BookingService,
    private title: Title,
    private meta: Meta,
    private locationStrategy: LocationStrategy,
    private productInfoProvider: CartProductInfoProvider
  ) {
    this.baseUrl = this.locationStrategy.getBaseHref();
  }

  public ngOnInit() {
    this.subscribtions = new Subscription();

    this.loaded = false;

    this.subscribtions.add(
      this.loadProduct()
        .pipe(tap(() => (this.loaded = true)))
        .subscribe()
    );

    this.subscribtions.add(this.loadPartnerConfig());
    this.subscribtions.add(this.booking.getLanguages().subscribe((resp: LanguageModel[]) => (this.languages = resp)));
  }

  public ngOnDestroy(): void {
    this.subscribtions.unsubscribe();
  }

  public trackByFn(index, option) {
    return option.id;
  }

  public applyConfig(config: IPartnerConfig) {
    this.pageConfig = { ...config };
    this.title.setTitle(this.pageConfig.title);
    this.meta.addTag({ name: 'description', content: this.pageConfig.description });
  }

  private loadProduct(): Observable<ProductModel> {
    return this.booking.getProducts().pipe(
      map((products) => {
        return products.map((product) => this.setImages(product));
      }),
      tap((products) => {
        this.products = products;
        const allOptions = [].concat.apply(
          [],
          products.map((product) =>
            product.options.map((option) => {
              option.product = product;
              return option;
            })
          )
        );
        this.options = allOptions;
        this.title.setTitle(this.products[0].name);
        this.storeProductInfo(products);
      }),
      switchMap(() => of(this.products[0]))
    );
  }

  private storeProductInfo(products: ProductModel[]) {
    const product = products[0];
    let productInfo: CartProductInfo = {
      productIds: products.map((product) => product.id),
      partnerPaymentSystemId: product.paymentSystemId,
      currency: product.currency,
      productTypeId: product.productTypeId,
      widgetContext: 'main',
    };

    this.productInfoProvider.store(productInfo);
  }

  private loadPartnerConfig() {
    return this.loadProduct()
      .pipe(switchMap((product) => this.booking.getPartnerCustomization(product.partnerId)))
      .subscribe((partnerConfig: IPartnerConfig) => {
        this.applyConfig(partnerConfig);
      });
  }

  private setImages(product: ProductModel): ProductModel {
    const heroImageUrl =
      product.imageUrl || `${this.baseUrl}assets/images/compressed/${ProductTypeId[product.productTypeId]}-head.jpg`;

    return {
      ...product,
      imageUrl: heroImageUrl,
      options: product.options.map((option: ProductOptionModel) => ({
        ...option,
        imageUrl:
          option.imageUrl ||
          `${this.baseUrl}assets/images/compressed/${ProductTypeId[product.productTypeId]}-default.jpg`,
      })),
    };
  }
}
